/**
 * Mill Lot Registry Page
 * ─────────────────────────────────────────────────────────────────────────────
 * The "Mill Lot No" (ML-2026-0001) is the spine of the entire bale management
 * system. Selecting / scanning a Mill Lot No auto-fills ALL department data:
 *   supplier, GRN, gate entry, cotton variety, crop year, HVI averages,
 *   category band, bale count, weight, cost per kg.
 *
 * The MillLotCard component at the bottom is re-exported and used by
 * GRN, QC, Laydown Planning, and IDT pages for the same auto-fill UX.
 */
import { useState, useEffect } from 'react'
import {
  App, Button, Card, Col, Descriptions, Divider, Form, Input, InputNumber,
  Modal, Row, Select, Space, Statistic, Table, Tag, Tooltip, Typography,
} from 'antd'
import {
  SearchOutlined, PlusOutlined, QrcodeOutlined, SyncOutlined,
  CheckCircleOutlined, ExclamationCircleOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { millLotApi } from '@/features/baleManagement/api'
import { plantsApi } from '@/features/masters/plants/api'
import { suppliersApi } from '@/features/gate/api'
import PageHeader from '@/shared/components/PageHeader'
import FormDrawer from '@/shared/components/FormDrawer'

const { Text, Title } = Typography

const LOT_STATUS_COLORS: Record<string, string> = {
  ACTIVE: 'green', PARTIALLY_CONSUMED: 'blue', CONSUMED: 'default',
  CLOSED: 'default', HOLD: 'orange', REJECTED: 'red',
}

const HVI_COLOR = (val: number | null, min: number, max: number) => {
  if (val === null || val === undefined) return 'default'
  return (val >= min && val <= max) ? 'success' : 'warning'
}

// ── MillLotCard — re-usable auto-fill card ─────────────────────────────────

export function MillLotCard({ lotNo, onLoaded }: { lotNo?: string; onLoaded?: (lot: any) => void }) {
  const [searchCode, setSearchCode] = useState(lotNo || '')
  const [activeLot, setActiveLot] = // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useState<any>(null)

  const lookup = useMutation({
    mutationFn: (code: string) => millLotApi.lookup(code),
    onSuccess: (lot) => {
      setActiveLot(lot)
      onLoaded?.(lot)
    },
  })

  useEffect(() => {
    if (lotNo && lotNo !== searchCode) {
      setSearchCode(lotNo)
      lookup.mutate(lotNo)
    }
  }, [lotNo])

  if (!activeLot) {
    return (
      <Card size="small" style={{ background: '#fafafa' }}>
        <Space>
          <QrcodeOutlined style={{ fontSize: 18, color: '#1677ff' }} />
          <Text type="secondary">Enter Mill Lot No to auto-fill all data</Text>
          <Input.Search
            placeholder="ML-2026-0001"
            value={searchCode}
            onChange={e => setSearchCode(e.target.value)}
            onSearch={code => lookup.mutate(code)}
            loading={lookup.isPending}
            style={{ width: 200 }}
            enterButton={<SearchOutlined />}
          />
        </Space>
      </Card>
    )
  }

  return (
    <Card
      size="small"
      style={{ background: '#f0f9ff', borderColor: '#91caff' }}
      title={
        <Space>
          <QrcodeOutlined style={{ color: '#1677ff' }} />
          <Text strong style={{ color: '#1677ff' }}>Mill Lot: {activeLot.mill_lot_no}</Text>
          <Tag color={LOT_STATUS_COLORS[activeLot.status] || 'default'}>{activeLot.status}</Tag>
          <Button size="small" icon={<SyncOutlined />} onClick={() => lookup.mutate(searchCode)}>
            Refresh
          </Button>
          <Button size="small" onClick={() => setActiveLot(null)}>Change Lot</Button>
        </Space>
      }
    >
      <Row gutter={16}>
        <Col span={6}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Supplier">{activeLot.supplier_name || '—'}</Descriptions.Item>
            <Descriptions.Item label="Variety">{activeLot.cotton_variety || '—'}</Descriptions.Item>
            <Descriptions.Item label="Crop Year">{activeLot.crop_year || '—'}</Descriptions.Item>
            <Descriptions.Item label="Origin">{activeLot.origin || '—'}</Descriptions.Item>
          </Descriptions>
        </Col>
        <Col span={6}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="GRN">{activeLot.grn_no || '—'}</Descriptions.Item>
            <Descriptions.Item label="Contract">{activeLot.contract_ref || '—'}</Descriptions.Item>
            <Descriptions.Item label="Supplier Lot">{activeLot.supplier_lot_ref || '—'}</Descriptions.Item>
            <Descriptions.Item label="Receipt">{activeLot.receipt_date || '—'}</Descriptions.Item>
          </Descriptions>
        </Col>
        <Col span={6}>
          <Row gutter={8}>
            <Col span={12}><Statistic title="Total Bales" value={activeLot.total_bales} /></Col>
            <Col span={12}><Statistic title="Approved" value={activeLot.approved_bales} valueStyle={{ color: '#52c41a' }} /></Col>
            <Col span={12}><Statistic title="Total Wt (kg)" value={Number(activeLot.total_weight_kg || 0).toFixed(1)} /></Col>
            <Col span={12}><Statistic title="Available" value={Number(activeLot.available_weight_kg || 0).toFixed(1)} /></Col>
          </Row>
        </Col>
        <Col span={6}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label="Avg Mic">{activeLot.avg_micronaire ? Number(activeLot.avg_micronaire).toFixed(2) : '—'}</Descriptions.Item>
            <Descriptions.Item label="Avg UHML">{activeLot.avg_uhml_mm ? `${Number(activeLot.avg_uhml_mm).toFixed(1)} mm` : '—'}</Descriptions.Item>
            <Descriptions.Item label="Avg Rd">{activeLot.avg_rd ? Number(activeLot.avg_rd).toFixed(1) : '—'}</Descriptions.Item>
            <Descriptions.Item label="Category">{activeLot.category_band
              ? <Tag color="blue">{activeLot.category_band}</Tag> : '—'}</Descriptions.Item>
          </Descriptions>
        </Col>
      </Row>
    </Card>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function MillLotPage() {
  const { message } = App.useApp()
  const qc = useQueryClient()
  const [form] = Form.useForm()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedLot, setSelectedLot] = // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useState<any>(null)
  const [plantFilter, setPlantFilter] = useState<string | undefined>()
  const [statusFilter, setStatusFilter] = useState<string | undefined>()
  const [searchText, setSearchText] = useState('')

  const { data: lots = [], isLoading } = useQuery({
    queryKey: ['mill-lots', plantFilter, statusFilter],
    queryFn: () => millLotApi.list({ plant_id: plantFilter, status: statusFilter, limit: 300 }),
  })
  const { data: plantsData } = useQuery({ queryKey: ['plants-all'], queryFn: () => plantsApi.list({ page: 1, page_size: 500 }) })
  const { data: suppliers } = useQuery({ queryKey: ['suppliers-all'], queryFn: () => suppliersApi.list({ limit: 500 }) })

  const plantOptions = plantsData?.items?.map((p: any) => ({ label: p.name, value: p.id })) ?? []
  const supplierOptions = suppliers?.items?.map((s: any) => ({ label: s.supplier_name, value: s.id })) ?? []

  const createMutation = useMutation({
    mutationFn: (vals: any) => millLotApi.create(vals),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['mill-lots'] }); message.success('Mill Lot created'); setDrawerOpen(false) },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: any) => millLotApi.update(id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['mill-lots'] }); message.success('Updated'); setSelectedLot(null) },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const refreshMutation = useMutation({
    mutationFn: (id: string) => millLotApi.refreshStats(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mill-lots'] })
      message.success('Lot stats refreshed (identity, landed cost, HVI averages)')
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const archiveMutation = useMutation({
    mutationFn: ({ id, force }: { id: string; force?: boolean }) =>
      millLotApi.archive(id, force ?? false),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mill-lots'] })
      message.success('Lot archived')
    },
    onError: (e: any) => message.error(e.response?.data?.detail || 'Failed'),
  })

  const filtered = (Array.isArray(lots) ? lots : []).filter((lot: any) =>
    !searchText || lot.mill_lot_no?.toLowerCase().includes(searchText.toLowerCase()) ||
    lot.supplier_name?.toLowerCase().includes(searchText.toLowerCase()) ||
    lot.cotton_variety?.toLowerCase().includes(searchText.toLowerCase())
  )

  const columns = [
    {
      title: 'Mill Lot No', dataIndex: 'mill_lot_no', key: 'mill_lot_no', width: 140,
      render: (v: string) => <Text strong style={{ fontFamily: 'monospace', color: '#1677ff' }}>{v}</Text>,
    },
    { title: 'Supplier', dataIndex: 'supplier_name', key: 'supplier', ellipsis: true },
    { title: 'Variety', dataIndex: 'cotton_variety', key: 'variety', width: 120 },
    { title: 'Crop Year', dataIndex: 'crop_year', key: 'crop_year', width: 90 },
    { title: 'Bales', dataIndex: 'total_bales', key: 'bales', width: 70, align: 'right' as const },
    { title: 'Wt (kg)', dataIndex: 'total_weight_kg', key: 'wt', width: 100, align: 'right' as const,
      render: (v: any) => Number(v || 0).toFixed(0) },
    { title: 'Approved', dataIndex: 'approved_bales', key: 'approved', width: 80, align: 'right' as const,
      render: (v: number, r: any) => (
        <Text type={v === r.total_bales && v > 0 ? 'success' : v > 0 ? undefined : 'secondary'}>{v}</Text>
      ) },
    { title: 'Avg Mic', dataIndex: 'avg_micronaire', key: 'mic', width: 80, align: 'right' as const,
      render: (v: any) => v ? Number(v).toFixed(2) : <Text type="secondary">—</Text> },
    { title: 'Avg UHML', dataIndex: 'avg_uhml_mm', key: 'uhml', width: 90, align: 'right' as const,
      render: (v: any) => v ? `${Number(v).toFixed(1)}mm` : <Text type="secondary">—</Text> },
    { title: 'Avg Str', dataIndex: 'avg_strength', key: 'str', width: 80, align: 'right' as const,
      render: (v: any) => v ? Number(v).toFixed(1) : <Text type="secondary">—</Text> },
    { title: 'Avg Rd', dataIndex: 'avg_rd', key: 'rd', width: 70, align: 'right' as const,
      render: (v: any) => v ? Number(v).toFixed(1) : <Text type="secondary">—</Text> },
    { title: 'CV Mic%', dataIndex: 'cv_micronaire', key: 'cv_mic', width: 80, align: 'right' as const,
      render: (v: any) => v ? (
        <Text type={Number(v) > 8 ? 'danger' : 'success'}>{Number(v).toFixed(1)}%</Text>
      ) : <Text type="secondary">—</Text> },
    { title: 'Band', dataIndex: 'category_band', key: 'band', width: 80,
      render: (v: string) => v ? <Tag color="blue">{v}</Tag> : '—' },
    { title: 'Cost/kg', dataIndex: 'landed_cost_per_kg', key: 'cost', width: 90, align: 'right' as const,
      render: (v: any) => v ? `₹${Number(v).toFixed(2)}` : <Text type="secondary">—</Text> },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 120,
      render: (v: string) => <Tag color={LOT_STATUS_COLORS[v] || 'default'}>{v.replace('_', ' ')}</Tag> },
    {
      title: 'Actions', key: 'actions', width: 260,
      render: (_: unknown, r: any) => {
        const isEmpty = (r.total_bales ?? 0) === 0
        return (
          <Space size={4} wrap>
            <Button size="small" onClick={() => {
              setSelectedLot(r)
              form.setFieldsValue({
                cotton_variety: r.cotton_variety, crop_year: r.crop_year,
                origin: r.origin, contract_ref: r.contract_ref,
                supplier_lot_ref: r.supplier_lot_ref,
                landed_cost_per_kg: r.landed_cost_per_kg,
                grade: r.grade, remarks: r.remarks,
              })
            }}>Edit</Button>
            <Button
              size="small"
              icon={<SyncOutlined />}
              loading={refreshMutation.isPending && refreshMutation.variables === r.id}
              onClick={() => refreshMutation.mutate(r.id)}
            >
              Refresh
            </Button>
            {r.status !== 'ARCHIVED' && (
              <Button
                size="small"
                danger
                onClick={() => {
                  Modal.confirm({
                    title: isEmpty ? 'Archive empty lot?' : 'Archive this Mill Lot?',
                    content: isEmpty
                      ? 'This lot has 0 bales — safe to archive.'
                      : `This lot has ${r.total_bales} bales. Force-archiving will keep bales linked but mark the lot as ARCHIVED.`,
                    okText: 'Archive',
                    okButtonProps: { danger: true },
                    onOk: () => archiveMutation.mutate({ id: r.id, force: !isEmpty }),
                  })
                }}
              >
                Archive
              </Button>
            )}
          </Space>
        )
      },
    },
  ]

  return (
    <div>
      <PageHeader
        title="Mill Lot Registry"
        subtitle="ML-YYYY-NNNN is the spine — auto-created from GRN. Click 'Refresh' on any lot to re-pull HVI + cost from PO/DO/lab."
      />

      {/* Summary */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        {[
          { label: 'Active Lots', val: (Array.isArray(lots) ? lots : []).filter((l: any) => l.status === 'ACTIVE').length, color: '#52c41a' },
          { label: 'Total Bales', val: (Array.isArray(lots) ? lots : []).reduce((a: number, l: any) => a + (l.total_bales || 0), 0), color: '#1677ff' },
          { label: 'QC Approved', val: (Array.isArray(lots) ? lots : []).reduce((a: number, l: any) => a + (l.approved_bales || 0), 0), color: '#52c41a' },
          { label: 'Total Weight (kg)', val: (Array.isArray(lots) ? lots : []).reduce((a: number, l: any) => a + Number(l.total_weight_kg || 0), 0).toFixed(0), color: undefined },
        ].map(s => (
          <Col span={6} key={s.label}>
            <Card size="small">
              <Statistic title={s.label} value={s.val} valueStyle={s.color ? { color: s.color } : undefined} />
            </Card>
          </Col>
        ))}
      </Row>

      <Card>
        <Row gutter={8} style={{ marginBottom: 16 }}>
          <Col>
            <Input.Search placeholder="Search lot / supplier / variety" prefix={<SearchOutlined />}
              style={{ width: 260 }} value={searchText} onChange={e => setSearchText(e.target.value)} allowClear />
          </Col>
          <Col>
            <Select allowClear placeholder="Plant" options={plantOptions} onChange={v => setPlantFilter(v)} style={{ width: 160 }} />
          </Col>
          <Col>
            <Select allowClear placeholder="Status"
              options={['ACTIVE', 'PARTIALLY_CONSUMED', 'CONSUMED', 'CLOSED', 'HOLD', 'REJECTED'].map(s => ({ label: s.replace('_', ' '), value: s }))}
              onChange={v => setStatusFilter(v)} style={{ width: 160 }} />
          </Col>
        </Row>

        <Table rowKey="id" columns={columns} dataSource={filtered} loading={isLoading}
          size="small" pagination={{ pageSize: 50 }} scroll={{ x: 1600 }} />
      </Card>

      {/* New Lot Drawer */}
      <FormDrawer title="New Mill Lot" open={drawerOpen} onClose={() => setDrawerOpen(false)}
        onSubmit={() => form.validateFields().then(v => createMutation.mutate(v))}
        loading={createMutation.isPending} form={form} width={560}
      >
        <Form.Item name="plant_id" label="Plant" rules={[{ required: true }]}>
          <Select options={plantOptions} />
        </Form.Item>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="mill_lot_no" label="Mill Lot No (auto if blank)">
              <Input placeholder="ML-2026-0001" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="supplier_id" label="Supplier">
              <Select options={supplierOptions} showSearch optionFilterProp="label" allowClear />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="cotton_variety" label="Cotton Variety">
              <Input placeholder="e.g. Shankar-6" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="crop_year" label="Crop Year">
              <Input placeholder="e.g. 2025-26" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="origin" label="Origin / Growth">
              <Input placeholder="e.g. Gujarat" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="contract_ref" label="Contract Ref">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="supplier_lot_ref" label="Supplier Lot Ref">
              <Input placeholder="Supplier's own lot code" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="landed_cost_per_kg" label="Landed Cost/kg (₹)">
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="invoice_ref" label="Invoice Ref"><Input /></Form.Item>
        <Form.Item name="remarks" label="Remarks"><Input.TextArea rows={2} /></Form.Item>
      </FormDrawer>

      {/* Edit Lot Drawer */}
      <FormDrawer title={`Edit Lot: ${selectedLot?.mill_lot_no}`} open={!!selectedLot}
        onClose={() => setSelectedLot(null)}
        onSubmit={() => form.validateFields().then(v => updateMutation.mutate({ id: selectedLot.id, data: v }))}
        loading={updateMutation.isPending} form={form} width={480}
      >
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="cotton_variety" label="Cotton Variety"><Input /></Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="crop_year" label="Crop Year"><Input /></Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="origin" label="Origin"><Input /></Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="contract_ref" label="Contract Ref"><Input /></Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="supplier_lot_ref" label="Supplier Lot Ref"><Input /></Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="landed_cost_per_kg" label="Landed Cost/kg (₹)">
              <InputNumber style={{ width: '100%' }} min={0} step={0.01} />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="remarks" label="Remarks"><Input.TextArea rows={2} /></Form.Item>
      </FormDrawer>
    </div>
  )
}
